import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import { cn } from '@/shared/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { Brain, ChevronDown, Clock } from 'lucide-react';

export const SPRING = { type: 'spring' as const, stiffness: 350, damping: 30 };

const collapseVariants = {
  open: {
    height: 'auto' as const,
    opacity: 1,
    transition: { ...SPRING, opacity: { duration: 0.2 } },
  },
  closed: { height: 0, opacity: 0, transition: { ...SPRING, opacity: { duration: 0.15 } } },
};

export function ProcessingBlock({
  summary,
  isProcessing,
  statusText,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
}: {
  readonly summary: string;
  readonly isProcessing: boolean;
  readonly statusText?: string;
  readonly defaultOpen?: boolean;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly children: ReactNode;
}) {
  const [localToggle, setLocalToggle] = useState<boolean | null>(null);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : (localToggle ?? defaultOpen);
  const toggle = () => {
    if (controlled) onOpenChange?.(!open);
    else setLocalToggle(!open);
  };
  const contentId = useId();

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={contentId}
        className="flex items-center gap-2 text-xs text-muted hover:text-foreground transition-colors cursor-pointer py-1 w-full text-left"
      >
        {isProcessing ? (
          <Clock size={14} className="shrink-0 animate-pulse" />
        ) : (
          <Brain size={14} className="shrink-0" />
        )}
        <span className="flex-1 truncate">{summary}</span>
        <motion.span
          className="shrink-0"
          animate={{ rotate: open ? 0 : -90 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={14} />
        </motion.span>
      </button>

      {!open && statusText && <p className="text-xs text-muted/70 pl-5.5 truncate">{statusText}</p>}

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={contentId}
            key="processing-content"
            variants={collapseVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="overflow-hidden"
          >
            <div className="pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function TimelineNode({
  icon,
  accent = false,
  error = false,
  children,
}: {
  readonly icon: ReactNode;
  readonly accent?: boolean;
  readonly error?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className="relative flex items-start min-h-6.5">
      <div
        className={cn(
          'absolute -left-9 top-0 w-6.5 h-6.5 rounded-full flex items-center justify-center border bg-background z-10',
          error
            ? 'border-danger/50 text-danger'
            : accent
              ? 'border-jade text-jade'
              : 'border-border/60 text-muted',
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0 pt-1">{children}</div>
    </div>
  );
}
